import { Vector2 } from 'three';

import { useFade } from 'contexts/Fade';
import { useTextures } from 'hooks/useTextures';

import logo from './assets/logo.svg';

const getPedestalPoints = (
  radius: number,
  height: number,
  gutters: number[]
) => {
  const bevel = height / 10;
  return [
    new Vector2(radius + bevel / 2, 0),
    new Vector2(radius + bevel / 4, bevel / 4),
    new Vector2(radius, bevel),
    new Vector2(radius - bevel, height - bevel),
    new Vector2(radius - bevel * 2, height),

    ...gutters.flatMap(gutter => [
      new Vector2(gutter, height),
      new Vector2(gutter, height - bevel),
      new Vector2(gutter - bevel, height - bevel),
      new Vector2(gutter - bevel, height),
    ]),

    new Vector2(0, height),
  ];
};

const BigH = 0.2;
const MediumH = 0.025;
const LittleH = 0.01;
const LATHE_SEGMENTS = 64;
const Big = getPedestalPoints(1.35, BigH, [0.925]);
const Medium = getPedestalPoints(1.2, MediumH, [0.8, 0.6]);

type Props = { opacity?: number } & React.JSX.IntrinsicElements['group'];

export const Pedestal = ({ ...props }: Props) => {
  const { addFadeMaterial } = useFade();

  const map = useTextures(logo);
  return (
    <group {...props}>
      <mesh castShadow receiveShadow>
        <latheGeometry args={[Big, LATHE_SEGMENTS]} />
        <meshStandardMaterial
          color="#222222"
          ref={addFadeMaterial}
          roughness={0.1}
          metalness={1}
        />
      </mesh>
      <mesh position-y={BigH} castShadow receiveShadow>
        <latheGeometry args={[Medium, LATHE_SEGMENTS]} />
        <meshStandardMaterial
          color="#333333"
          ref={addFadeMaterial}
          metalness={1}
          roughness={0.25}
        />
      </mesh>
      <mesh position-y={BigH + MediumH} castShadow receiveShadow>
        <cylinderGeometry args={[0.95, 0.95, LittleH, LATHE_SEGMENTS]} />
        <meshStandardMaterial
          ref={addFadeMaterial}
          metalness={1}
          color="#333333"
          emissive="#ffffff"
          emissiveIntensity={2.5}
          roughness={0.5}
          emissiveMap={map}
        />
      </mesh>
      <directionalLight
        color={0xffffff}
        intensity={1}
        // directionalLight target 0,0,0
        position={[0, -1, 1]}
        castShadow
      />
      <group position-y={0.235}>{props.children}</group>
    </group>
  );
};
